import { Alert, Box, Button, CircularProgress, Grid } from "@mui/material";
import { Add, FilterList } from "@mui/icons-material";

import { PortfolioCard, useFetchPortfolios } from "../index";
import { useNavigation } from "../../../hooks/useNavigation";
import { SearchField, PaginationControls, DrawerControl, FilterSideBar } from "../../../shared/components";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const Portfolio = () => {

  const { can } = useCapabilities();

  const isAllowedToAddPortfolio = can("operational_delivery:portfolio_manager:portfolio:add");
  const isAllowedToBulkUploadProjects = can("operational_delivery:portfolio_manager:bulk_upload_projects");

  const {
    isLoading, isError, portfolios, pagination, searchDraft, isOpenFilter, tempFilters,
    handleChangeSearch, handleClear, handleSearch, changePage, changeRowsPerPage,
    handleCloseFilter, handleOpenFilter, handleResetFilters, handleApplyFilters, handleUpdateTempFilters
  } = useFetchPortfolios();

  const { goToPortfolioProjects, goToPortfolioDetails, goToAddPortfolio } = useNavigation();

  const onViewProjects = (project: { portfolio_name: string, id: number }) => {
    goToPortfolioProjects(project.portfolio_name, project.id);
  };

  const onViewDetails = (project: { portfolio_name: string, id: number }) => {
    goToPortfolioDetails(project.portfolio_name, project.id);
  };

  if (isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    )
  };

  if (isError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Alert
          severity="warning"
          sx={{
            width: "auto",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {isError}
        </Alert>
      </Box>
    )
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={1.5} py={2}>

      <Box display="flex" alignItems="start" gap={2} px={2}>

        <SearchField
          label="Portfolio Name or Reference"
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={handleClear}
          handleApplySearch={handleSearch}
        />

        <Button
          startIcon={<FilterList />}
          variant="text"
          color="inherit"
          sx={{ marginLeft: "auto" }}
          onClick={handleOpenFilter}
        >
          FILTERS
        </Button>

        {isAllowedToAddPortfolio && (
          <Button
            startIcon={<Add />}
            onClick={goToAddPortfolio}
          >
            ADD PORTFOLIO
          </Button>
        )}

      </Box>

      <Box flex={1} overflow="auto" px={2} pb={2}>
        {portfolios?.length === 0 ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Alert
              severity="warning"
              sx={{
                width: "auto",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              No portfolio found. You can add a new project to get started.
            </Alert>
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="start"
            alignItems="stretch"
            alignContent="stretch"
          >
            {portfolios.map((
              project: {
                id: number;
                portfolio_id: number;
                portfolio_name: string;
                address: string;
                phase_template: string;
                project_status: string;
                portfolio_reference: string;
                rag_status: {
                  time: "green" | "red" | "amber";
                  cost: "green" | "red" | "amber";
                  quality: "green" | "red" | "amber";
                  overall: "green" | "red" | "amber";
                };
                projects: {
                  total_projects: number;
                  red: number;
                  amber: number;
                  green: number;
                };
              },
              index: any,
            ) => (
              <Grid key={index} size={{ sm: 12, xs: 12, md: 3, lg: 3 }}>
                <PortfolioCard
                  portfolioId={project.portfolio_id}
                  title={project.portfolio_name}
                  projectId={project.portfolio_reference}
                  projectCount={project.projects.total_projects}
                  ragStatus={project.rag_status}
                  onViewProjects={() => onViewProjects(project)}
                  onViewDetails={() => onViewDetails(project)}
                  counts={{
                    green: project.projects.green,
                    orange: project.projects.amber,
                    red: project.projects.red,
                  }}
                />
              </Grid>
            ),
            )}
          </Grid>
        )}
      </Box>

      <PaginationControls
        total={pagination.total}
        rowsPerPage={pagination.limit}
        currentPage={pagination.page}
        handlePageChange={changePage}
        handleRowsPerPage={changeRowsPerPage}
      />

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar onClose={handleCloseFilter} />
      </DrawerControl>
    </Box>
  );
}